import React, { Component } from 'react';
import Dropzone from 'react-dropzone';
import CONSTANTS from '@components-shared/constants';
import { InputFieldWrapper } from '@components-shared/forms/FieldLabel';
import { keyMap } from '@locales/lokalise/keyMap';
import { fileValidationService } from '@utils/library/fileValidation.service';
import cn from 'classnames';
import { connectToContext } from 'contextStore';
import { UploadFileInputPreview } from './UploadFileImputPrevew';
import { IUploadMultiFileInputProps, UploadFileType } from './types';

interface IUploadFileInputState {
  hasError: boolean;
}

const MAX_SIZE = 52428800;

class UploadMultiFileInput extends Component<IUploadMultiFileInputProps, IUploadFileInputState> {
  state: IUploadFileInputState = {
    hasError: false,
  };

  onDrop = async (files: File[]): Promise<void> => {
    const { onDropHandler } = this.props;
    const isValid = await fileValidationService.isValidFiles(files);

    if (!isValid) {
      return;
    }

    if (onDropHandler) {
      onDropHandler(files);
    }

    this.setState({ hasError: !files }, () => this.props.onChange(this.props.name, files || null));
  };

  onClear = (): void => {
    const { onChange, onDeleteHandler } = this.props;

    onChange(this.props.name, null);
    this.setState({ hasError: false });

    onDeleteHandler?.();
  };

  renderImages = (value: UploadFileType[] | null, fileItems: File[] | null): JSX.Element | undefined => {
    if (value === null) return;

    const images = value.map((item, idx) => {
      let url: string | undefined;

      if (fileItems && fileItems.length) {
        url = URL.createObjectURL(fileItems[idx]);
      }

      return (
        <UploadFileInputPreview
          key={idx}
          preattachedFile={this.props.preattachedFile}
          value={item}
          imagePreviewUrl={url}
        />
      );
    });

    return <div className="form__input-previewContainer">{images}</div>;
  };

  render(): JSX.Element {
    const { hasError } = this.state;
    const {
      title,
      name,
      valid,
      validationMessage,
      value,
      attributes,
      hintMessage,
      accept,
      maxSize,
      required,
      className,
      testID = 'formUploadFileInput',
      tooltip,
      i18next,
    } = this.props;
    const acceptString = accept || CONSTANTS.ACCEPTED_FILE_TYPE;

    const label = title || keyMap.formsChooseFile;
    const isDropzoneDisabled = (attributes && attributes.disabled) || false;

    return (
      <InputFieldWrapper
        title={label}
        name={name}
        required={required}
        className={className}
        hintMessage={hintMessage}
        valid={valid}
        validationMessage={validationMessage}
        tooltip={tooltip}
        testID={testID}
        preventLabelEvent={true}>
        <>
          <Dropzone
            accept={acceptString}
            onDrop={this.onDrop}
            maxSize={maxSize || MAX_SIZE}
            disabled={isDropzoneDisabled}>
            {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, acceptedFiles }) => {
              const additionalClasses = cn('form__drop-input', {
                'dropzone--active': isDragActive,
                'dropzone--accept': isDragAccept,
                'dropzone--reject': isDragReject,
                'dropzone--disabled': isDropzoneDisabled,
              });

              return (
                <div {...getRootProps({ className: additionalClasses })}>
                  <div className={cn('form__input', { invalid: !valid || hasError })}>
                    {this.renderImages(value, acceptedFiles)}
                  </div>
                  <div className="form__button">{i18next.t(keyMap.formsBrowse)}</div>
                  <input {...getInputProps()} />
                </div>
              );
            }}
          </Dropzone>
          {value !== null && value.length !== 0 && (
            <span className="form__clear" onClick={this.onClear}>
              {i18next.t(keyMap.sharedActionsClear)}
            </span>
          )}
        </>
      </InputFieldWrapper>
    );
  }
}
const UploadMultiFileInputWithContext = connectToContext(UploadMultiFileInput);

export { UploadMultiFileInputWithContext as UploadMultiFileInput };
